import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import QRCode from "qrcode";
import { Database, Settings2, UserRound, UsersRound } from "lucide-react";
import {
  DEFAULT_USER_SETTINGS,
  type Household,
  type Membership,
  type Theme,
  type UserProfile,
  type UserSettings,
} from "@lifelog/shared";
import { api, apiBaseUrl, ApiError, isOfflineError } from "../lib/api.js";
import {
  confirmUserAttribute,
  emailFromIdToken,
  fetchMFAPreference,
  getIdToken,
  setUpTOTP,
  updateMFAPreference,
  updatePassword,
  updateUserAttribute,
  verifyTOTPSetup,
} from "../lib/auth.js";
import { downloadExportBundle } from "../lib/export.js";
import { applyTheme } from "../lib/theme.js";
import { ConfirmDialog } from "./ModalShell.js";
import { PageSkeleton } from "./Skeleton.js";

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const TIMEZONES = [
  "America/Denver",
  "America/Phoenix",
  "America/Chicago",
  "America/New_York",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
  "Europe/London",
  "Europe/Paris",
];

type SettingsTab = "account" | "preferences" | "household" | "data";

const SETTINGS_TABS: { id: SettingsTab; label: string; Icon: typeof UserRound; ownerOnly?: boolean }[] = [
  { id: "account", label: "Account", Icon: UserRound },
  { id: "preferences", label: "Preferences", Icon: Settings2 },
  { id: "household", label: "Household", Icon: UsersRound, ownerOnly: true },
  { id: "data", label: "Data & privacy", Icon: Database },
];

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-5">
      <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      {children}
    </section>
  );
}

export function SettingsPane() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [me, setMe] = useState<UserProfile | null>(null);
  const [household, setHousehold] = useState<Household | null>(null);
  const [members, setMembers] = useState<Membership[]>([]);
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_USER_SETTINGS);
  const [signInEmail, setSignInEmail] = useState("");

  const [displayName, setDisplayName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailConfirming, setEmailConfirming] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [totpEnabled, setTotpEnabled] = useState(false);
  const [setupUri, setSetupUri] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");

  const [hhName, setHhName] = useState("");
  const [hhTz, setHhTz] = useState("America/Denver");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [revokeUserId, setRevokeUserId] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [hasIcsToken, setHasIcsToken] = useState(false);
  const [icsUrl, setIcsUrl] = useState<string | null>(null);
  const [icsCopied, setIcsCopied] = useState(false);
  const [icsRotateOpen, setIcsRotateOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<SettingsTab>("account");

  const isOwner = Boolean(me && household && me.userId === household.ownerUserId);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [meRes, householdRes, membersRes, settingsRes, icsRes] = await Promise.all([
        api<{ profile: UserProfile }>("/me"),
        api<Household>("/household"),
        api<{ members: Membership[] }>("/household/members"),
        api<UserSettings>("/settings"),
        api<{ hasToken: boolean }>("/ics-token"),
      ]);
      setMe(meRes.profile);
      setHousehold(householdRes);
      setMembers(membersRes.members);
      setSettings(settingsRes);
      setHasIcsToken(icsRes.hasToken);
      setIcsUrl(null);
      setDisplayName(meRes.profile.displayName);
      setHhName(householdRes.name);
      setHhTz(householdRes.timezone);
      applyTheme(settingsRes.theme);
      const token = await getIdToken();
      setSignInEmail(token ? emailFromIdToken(token) ?? meRes.profile.email : meRes.profile.email);
      try {
        const pref = await fetchMFAPreference();
        const enabled = pref.enabled;
        setTotpEnabled(
          (Array.isArray(enabled) && enabled.includes("TOTP")) || pref.preferred === "TOTP",
        );
      } catch {
        setTotpEnabled(false);
      }
    } catch (err) {
      if (isOfflineError(err)) setError("You're offline. Try again when you're back online.");
      else if (!(err instanceof ApiError && err.status === 401)) {
        setError(err instanceof Error ? err.message : "Couldn't load settings.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function flash(ok: string) {
    setMessage(ok);
    setError(null);
  }

  async function saveSettingsPatch(patch: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    try {
      const next = await api<UserSettings>("/settings", {
        method: "PUT",
        json: { ...settings, ...patch },
      });
      setSettings(next);
      applyTheme(next.theme);
      flash("Saved.");
      return next;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save.");
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function saveDisplayName(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ profile: UserProfile }>("/me", {
        method: "PATCH",
        json: { displayName },
      });
      setMe(res.profile);
      flash("Display name saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save display name.");
    } finally {
      setBusy(false);
    }
  }

  async function startEmailChange(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await updateUserAttribute({ userAttribute: { attributeKey: "email", value: newEmail.trim() } });
      setEmailConfirming(true);
      flash("Check that inbox for a verification code.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't start an email change.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmEmailChange(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await confirmUserAttribute({ userAttributeKey: "email", confirmationCode: emailCode.trim() });
      setEmailConfirming(false);
      setEmailCode("");
      setNewEmail("");
      setSignInEmail(newEmail.trim());
      flash("Sign-in email updated.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code didn't work.");
    } finally {
      setBusy(false);
    }
  }

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("New passwords don't match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await updatePassword({ oldPassword, newPassword });
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      flash("Password updated.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't change password.");
    } finally {
      setBusy(false);
    }
  }

  async function startEnroll() {
    setBusy(true);
    setError(null);
    try {
      const details = await setUpTOTP();
      const token = await getIdToken();
      const email = token ? emailFromIdToken(token) : undefined;
      const uri = details.getSetupUri("Life Logger", email).toString();
      setSecret(details.sharedSecret);
      setSetupUri(uri);
      setQrDataUrl(await QRCode.toDataURL(uri, { width: 192, margin: 1 }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start authenticator setup.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnroll(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifyTOTPSetup({ code: totpCode.trim() });
      await updateMFAPreference({ totp: "PREFERRED" });
      setSecret(null);
      setSetupUri(null);
      setQrDataUrl(null);
      setTotpCode("");
      setTotpEnabled(true);
      flash("Authenticator enrolled. You'll be asked for a code the next time you sign in.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not verify that code.");
    } finally {
      setBusy(false);
    }
  }

  async function disableTotp() {
    setBusy(true);
    setError(null);
    try {
      await updateMFAPreference({ totp: "DISABLED" });
      setTotpEnabled(false);
      flash("Authenticator disabled.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't disable the authenticator.");
    } finally {
      setBusy(false);
    }
  }

  async function saveHousehold(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const next = await api<Household>("/household", {
        method: "PATCH",
        json: { name: hhName, timezone: hhTz },
      });
      setHousehold(next);
      window.dispatchEvent(new Event("lifelogger:shell-refresh"));
      flash("Household saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save household.");
    } finally {
      setBusy(false);
    }
  }

  async function sendInvite(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/household/invites", {
        method: "POST",
        json: { email: inviteEmail.trim(), displayName: inviteName.trim() || undefined },
      });
      setInviteEmail("");
      setInviteName("");
      const membersRes = await api<{ members: Membership[] }>("/household/members");
      setMembers(membersRes.members);
      flash("Invite created. Add the address to /lifelogger/allowedEmails if sign-up was rejected.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't invite that person.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmRevoke() {
    if (!revokeUserId) return;
    setBusy(true);
    try {
      await api(`/household/members/${encodeURIComponent(revokeUserId)}`, { method: "DELETE" });
      setMembers((prev) => prev.filter((m) => m.userId !== revokeUserId));
      setRevokeUserId(null);
      flash("Member removed.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't remove that member.");
      setRevokeUserId(null);
    } finally {
      setBusy(false);
    }
  }

  async function exportAll() {
    setBusy(true);
    setError(null);
    try {
      const from = new Date(Date.now() - 3 * 365 * 24 * 60 * 60 * 1000).toISOString();
      const to = new Date().toISOString();
      const [logsRes, typesRes] = await Promise.all([
        api<{ logs: import("@lifelog/shared").ActivityLog[] }>(
          `/logs?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=500`,
        ),
        api<{ types: import("@lifelog/shared").ActivityType[] }>("/activity-types"),
      ]);
      const names = new Map(members.map((m) => [m.userId, m.displayName]));
      downloadExportBundle(typesRes.types, logsRes.logs, (id) => (id ? names.get(id) ?? id : ""));
      flash("Export downloaded.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't export.");
    } finally {
      setBusy(false);
    }
  }

  async function createOrRotateIcs() {
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ token: string }>("/ics/token", { method: "POST" });
      const url = `${apiBaseUrl()}/ics/${res.token}`;
      setIcsUrl(url);
      setHasIcsToken(true);
      setIcsRotateOpen(false);
      flash("Calendar feed URL created. Copy it now — it is shown only once.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't create the calendar feed.");
      setIcsRotateOpen(false);
    } finally {
      setBusy(false);
    }
  }

  async function copyIcsUrl() {
    if (!icsUrl) return;
    await navigator.clipboard.writeText(icsUrl);
    setIcsCopied(true);
    window.setTimeout(() => setIcsCopied(false), 2000);
  }

  async function confirmDeleteRequest() {
    setBusy(true);
    try {
      const res = await api<{ message: string }>("/me/delete-request", { method: "POST" });
      setDeleteOpen(false);
      flash(res.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't record that request.");
      setDeleteOpen(false);
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-lg p-4">
        <h1 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Settings</h1>
        <PageSkeleton />
      </div>
    );
  }

  const hours = Array.from({ length: 24 }, (_, h) => h);
  const timezones = TIMEZONES.includes(hhTz) ? TIMEZONES : [hhTz, ...TIMEZONES];

  return (
    <div className="settings-pane mx-auto max-w-2xl space-y-5 p-4 sm:p-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">Settings</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Manage your account, preferences, and household from one place.
        </p>
      </div>
      {message && <p className="text-sm text-green-600 dark:text-green-400">{message}</p>}
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div
        className="-mx-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0"
        role="tablist"
        aria-label="Settings categories"
      >
        <div className="inline-flex min-w-max gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-900">
          {SETTINGS_TABS.filter((tab) => !tab.ownerOnly || isOwner).map(({ id, label, Icon }) => {
            const selected = activeTab === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                id={`settings-tab-${id}`}
                aria-controls={`settings-panel-${id}`}
                aria-selected={selected}
                onClick={() => setActiveTab(id)}
                className={[
                  "flex min-h-10 items-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors",
                  selected
                    ? "bg-(--accent) text-(--accent-contrast) shadow-sm"
                    : "text-gray-600 hover:bg-white hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100",
                ].join(" ")}
              >
                <Icon size={16} aria-hidden="true" />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div role="tabpanel" id={`settings-panel-${activeTab}`} aria-labelledby={`settings-tab-${activeTab}`}>
      {activeTab === "account" && (
        <Section title="Account">
        <form onSubmit={saveDisplayName} className="space-y-2">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
            Display name
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className={`${inputCls} mt-1`}
            />
          </label>
          <button
            type="submit"
            disabled={busy || !displayName.trim()}
            className="rounded-md bg-(--accent) px-3 py-2 text-sm font-medium text-(--accent-contrast) disabled:opacity-50"
          >
            Save name
          </button>
        </form>
        <p className="text-xs text-gray-500 dark:text-gray-400">Sign-in email: {signInEmail || "—"}</p>
        {!emailConfirming ? (
          <form onSubmit={startEmailChange} className="space-y-2">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Change sign-in email
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className={`${inputCls} mt-1`}
                autoComplete="email"
              />
            </label>
            <button
              type="submit"
              disabled={busy || !newEmail.trim()}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 disabled:opacity-50"
            >
              Send verification code
            </button>
          </form>
        ) : (
          <form onSubmit={confirmEmailChange} className="space-y-2">
            <input
              value={emailCode}
              onChange={(e) => setEmailCode(e.target.value)}
              placeholder="Verification code"
              className={inputCls}
              autoComplete="one-time-code"
            />
            <button
              type="submit"
              disabled={busy || !emailCode.trim()}
              className="rounded-md bg-(--accent) px-3 py-2 text-sm font-medium text-(--accent-contrast) disabled:opacity-50"
            >
              Confirm email
            </button>
          </form>
        )}
        <form onSubmit={changePassword} className="space-y-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Change password</p>
          <input
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            placeholder="Current password"
            className={inputCls}
            autoComplete="current-password"
          />
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="New password (12+ chars)"
            className={inputCls}
            autoComplete="new-password"
          />
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            className={inputCls}
            autoComplete="new-password"
          />
          <button
            type="submit"
            disabled={busy || !oldPassword || !newPassword}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 disabled:opacity-50"
          >
            Update password
          </button>
        </form>
        <div className="space-y-2 border-t border-gray-100 pt-3 dark:border-gray-800">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Two-factor authentication {totpEnabled ? "(on)" : "(off)"}
          </p>
          {totpEnabled && !setupUri && (
            <button
              type="button"
              onClick={() => void disableTotp()}
              disabled={busy}
              className="rounded-md border border-red-300 px-3 py-2 text-sm text-red-700 disabled:opacity-50 dark:border-red-800 dark:text-red-400"
            >
              Disable authenticator
            </button>
          )}
          {!totpEnabled && !setupUri && (
            <button
              type="button"
              onClick={() => void startEnroll()}
              disabled={busy}
              className="rounded-md bg-(--accent) px-3 py-2 text-sm font-medium text-(--accent-contrast) disabled:opacity-50"
            >
              Set up authenticator
            </button>
          )}
          {setupUri && secret && (
            <form onSubmit={confirmEnroll} className="space-y-3">
              {qrDataUrl && (
                <img src={qrDataUrl} alt="Authenticator QR code" className="h-48 w-48 rounded-md bg-white p-2" />
              )}
              <p className="text-sm text-gray-600 dark:text-gray-300">
                On a phone?{" "}
                <a href={setupUri} className="text-(--accent) underline">
                  Tap to open in your authenticator app
                </a>
              </p>
              <div className="rounded bg-gray-100 px-3 py-2 dark:bg-gray-800">
                <span className="block break-all font-mono text-xs text-gray-900 select-all dark:text-gray-100">
                  {secret}
                </span>
              </div>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                autoComplete="one-time-code"
                placeholder="000000"
                className={inputCls}
              />
              <button
                type="submit"
                disabled={busy || totpCode.trim().length !== 6}
                className="rounded-md bg-(--accent) px-3 py-2 text-sm font-medium text-(--accent-contrast) disabled:opacity-50"
              >
                Verify and enable
              </button>
            </form>
          )}
        </div>
        </Section>
      )}

      {activeTab === "preferences" && (
        <div className="space-y-4">
          <Section title="Notifications">
        <label className="flex items-center justify-between gap-3 text-sm text-gray-800 dark:text-gray-200">
          Weekly digest
          <input
            type="checkbox"
            checked={settings.weeklyDigestEnabled}
            onChange={(e) => void saveSettingsPatch({ weeklyDigestEnabled: e.target.checked })}
            disabled={busy}
          />
        </label>
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
          Digest day
          <select
            value={settings.weeklyDigestDay}
            onChange={(e) => void saveSettingsPatch({ weeklyDigestDay: Number(e.target.value) })}
            disabled={busy || !settings.weeklyDigestEnabled}
            className={`${inputCls} mt-1`}
          >
            {WEEKDAYS.map((label, i) => (
              <option key={label} value={i}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center justify-between gap-3 text-sm text-gray-800 dark:text-gray-200">
          Overdue nudges
          <input
            type="checkbox"
            checked={settings.overdueNudgeEnabled}
            onChange={(e) => void saveSettingsPatch({ overdueNudgeEnabled: e.target.checked })}
            disabled={busy}
          />
        </label>
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
          Nudge hour
          <select
            value={settings.overdueNudgeHour}
            onChange={(e) => void saveSettingsPatch({ overdueNudgeHour: Number(e.target.value) })}
            disabled={busy || !settings.overdueNudgeEnabled}
            className={`${inputCls} mt-1`}
          >
            {hours.map((h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, "0")}:00
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
          Notification email
          <input
            type="email"
            defaultValue={settings.emailAddress ?? ""}
            key={settings.emailAddress ?? ""}
            onBlur={(e) => {
              const next = e.target.value.trim() || null;
              if ((next ?? undefined) !== settings.emailAddress) void saveSettingsPatch({ emailAddress: next });
            }}
            className={`${inputCls} mt-1`}
            placeholder={signInEmail || "you@example.com"}
          />
        </label>
      </Section>

          <Section title="Appearance">
        <div className="flex gap-2" role="radiogroup" aria-label="Theme">
          {(["system", "light", "dark"] as Theme[]).map((theme) => (
            <button
              key={theme}
              type="button"
              onClick={() => void saveSettingsPatch({ theme })}
              disabled={busy}
              className={
                "min-h-11 flex-1 rounded-lg capitalize text-sm " +
                (settings.theme === theme
                  ? "bg-(--accent) text-(--accent-contrast)"
                  : "border border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-300")
              }
            >
              {theme}
            </button>
          ))}
        </div>
      </Section>

          <Section title="Logging defaults">
        <label className="flex items-center justify-between gap-3 text-sm text-gray-800 dark:text-gray-200">
          Performed by me
          <input
            type="checkbox"
            checked={settings.defaultPerformedBySelf}
            onChange={(e) => void saveSettingsPatch({ defaultPerformedBySelf: e.target.checked })}
            disabled={busy}
          />
        </label>
        <label className="flex items-center justify-between gap-3 text-sm text-gray-800 dark:text-gray-200">
          Always open the log modal
          <input
            type="checkbox"
            checked={settings.oneTapOpensModal}
            onChange={(e) => void saveSettingsPatch({ oneTapOpensModal: e.target.checked })}
            disabled={busy}
          />
        </label>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Off: types with no required fields log immediately. On: every dashboard tap opens the form.
        </p>
      </Section>
        </div>
      )}

      {activeTab === "household" && isOwner && household && (
        <Section title="Household">
          <form onSubmit={saveHousehold} className="space-y-2">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Name
              <input value={hhName} onChange={(e) => setHhName(e.target.value)} className={`${inputCls} mt-1`} />
            </label>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Timezone
              <select value={hhTz} onChange={(e) => setHhTz(e.target.value)} className={`${inputCls} mt-1`}>
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              disabled={busy}
              className="rounded-md bg-(--accent) px-3 py-2 text-sm font-medium text-(--accent-contrast) disabled:opacity-50"
            >
              Save household
            </button>
          </form>
          <ul className="divide-y divide-gray-100 text-sm dark:divide-gray-800">
            {members.map((m) => (
              <li key={m.userId} className="flex items-center gap-2 py-2">
                <span className="min-w-0 flex-1 truncate text-gray-800 dark:text-gray-200">
                  {m.displayName}
                  <span className="ml-2 text-xs text-gray-400">
                    {m.role === "owner" ? "owner" : "co-owner"} · {m.email}
                  </span>
                </span>
                {m.role !== "owner" && (
                  <button
                    type="button"
                    onClick={() => setRevokeUserId(m.userId)}
                    className="text-xs text-red-600 dark:text-red-400"
                  >
                    Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
          <form onSubmit={sendInvite} className="space-y-2">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Invite a co-owner</p>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="email@example.com"
              className={inputCls}
            />
            <input
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              placeholder="Display name (optional)"
              className={inputCls}
            />
            <button
              type="submit"
              disabled={busy || !inviteEmail.trim()}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 disabled:opacity-50"
            >
              Send invite
            </button>
          </form>
        </Section>
      )}

      {activeTab === "data" && (
        <Section title="Data & privacy">
        <button
          type="button"
          onClick={() => void exportAll()}
          disabled={busy}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 disabled:opacity-50"
        >
          Export bundle
        </button>
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Calendar feed</p>
          {icsUrl ? (
            <>
              <input readOnly value={icsUrl} className={inputCls} />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void copyIcsUrl()}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700"
                >
                  {icsCopied ? "Copied" : "Copy URL"}
                </button>
                <button
                  type="button"
                  onClick={() => setIcsRotateOpen(true)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700"
                >
                  Rotate token
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {hasIcsToken
                  ? "A feed is active. The URL is shown only when you create or rotate it."
                  : "Subscribe in Google Calendar or Apple Calendar with a secret URL."}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => (hasIcsToken ? setIcsRotateOpen(true) : void createOrRotateIcs())}
                  disabled={busy}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 disabled:opacity-50"
                >
                  {hasIcsToken ? "Rotate token" : "Create calendar feed"}
                </button>
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => setDeleteOpen(true)}
          className="rounded-md border border-red-300 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:text-red-400"
        >
          Request delete-my-data
        </button>
        </Section>
      )}
      </div>

      {revokeUserId && (
        <ConfirmDialog
          title="Remove this member?"
          message="They will lose access to this household."
          confirmLabel="Remove"
          destructive
          busy={busy}
          onConfirm={() => void confirmRevoke()}
          onCancel={() => setRevokeUserId(null)}
        />
      )}
      {icsRotateOpen && (
        <ConfirmDialog
          title="Rotate the calendar feed?"
          message="The current URL will stop working immediately. You will get a new URL to copy."
          confirmLabel="Rotate"
          destructive
          busy={busy}
          onConfirm={() => void createOrRotateIcs()}
          onCancel={() => setIcsRotateOpen(false)}
        />
      )}
      {deleteOpen && (
        <ConfirmDialog
          title="Request data deletion?"
          message="This records a request to remove your logs and account. It does not delete anything immediately."
          confirmLabel="Request deletion"
          destructive
          busy={busy}
          onConfirm={() => void confirmDeleteRequest()}
          onCancel={() => setDeleteOpen(false)}
        />
      )}
    </div>
  );
}
